/*
 * Whether any AI chat waits for the reader: Home's `hasItems`, asked from the first paint on every
 * change of the model (S13 §2.7). It lives apart from `waiting.ts`, which words the rows, so the entry
 * chunk carries this handful of lines rather than every sentence, glyph and door of "Waiting for you"
 * (S13 §1's entry weight, `core/modules/entryClosure.test.ts`).
 */

import type { Model } from "../../../core/store/model.ts"
import { aichatSlice, type AichatSlice } from "../sync/slice.ts"
import {
	WaitAccessRequest,
	WaitAssigned,
	WaitCeiling,
	WaitChecksFailed,
	WaitDisk,
	WaitFinished,
	WaitHeld,
	WaitInterrupted,
	WaitLimit,
	WaitLogin,
	WaitModifyRequest,
	WaitNeedsIntegration,
	WaitPartlyPublished,
	WaitPlan,
	WaitQuestion,
	WaitReady,
	WaitReviewRequested,
	WaitStale,
	WaitTooLong,
	type ChatRow,
	type WaitRow,
} from "../wire/aichat.gen.ts"

/**
 * Every reason S01 §4.5 names that "Waiting for you" shows — the keys of `waiting.ts`'s looks, which
 * a test holds equal to this list. A reason this build does not know waits for nobody here.
 */
export const SHOWN_WAITS: ReadonlySet<string> = new Set([
	WaitQuestion,
	WaitPlan,
	WaitModifyRequest,
	WaitLimit,
	WaitLogin,
	WaitInterrupted,
	WaitTooLong,
	WaitCeiling,
	WaitDisk,
	WaitHeld,
	WaitFinished,
	WaitPartlyPublished,
	WaitReviewRequested,
	WaitReady,
	WaitChecksFailed,
	WaitNeedsIntegration,
	WaitAccessRequest,
	WaitStale,
	WaitAssigned,
])

/**
 * A finished answer the reader has looked at since waits no more — the server's own rule (S01 §4.5).
 * The reader state says so the moment the page reads the chat; the row says it only at the chat's
 * next change, so the list reads the two together.
 */
export function lookedAtSince(slice: AichatSlice, row: ChatRow, wait: WaitRow): boolean {
	return (
		wait.reason === WaitFinished && (slice.readers[row.chatId]?.openedAt ?? 0) > (wait.since ?? 0)
	)
}

/**
 * Whether anything waits for the reader: Home's `hasItems` (it says "You’re up to date" only when
 * no section answers yes). It reads the rows and never words them, so it is cheap on every change.
 */
export function hasWaiting(model: Model): boolean {
	const me = model.me?.id
	if (me === undefined) return false
	const slice = aichatSlice(model)
	return Object.values(slice.chats).some((row) =>
		row.waiting.some(
			(wait) =>
				SHOWN_WAITS.has(wait.reason) &&
				wait.userIds.includes(me) &&
				!lookedAtSince(slice, row, wait),
		),
	)
}
